import {
  MiddlewareAPI,
  isRejectedWithValue,
  Middleware,
} from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { baseApi } from 'common/store/baseApi';
import { clearCredentials, refreshToken } from 'features/login/authSlice';
import { captureError, EXPECTED_CLIENT_ERROR_STATUSES } from 'common/utils/sentry';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';

// redux-persist's default storage key (see store.ts persistConfig key: 'root').
const PERSIST_STORAGE_KEY = 'persist:root';

// Synchronously read the persisted bearer straight from localStorage. Used on a
// 401 to detect a token that was rotated (by another tab, or an earlier request
// in this tab) after the failing request was sent. Returns null on any miss /
// malformed value — a null result simply means "no newer token", i.e. log out.
const readPersistedToken = (): string | null => {
  try {
    const raw = localStorage.getItem(PERSIST_STORAGE_KEY);
    if (!raw) return null;
    const root = JSON.parse(raw);
    if (typeof root?.auth !== 'string') return null;
    const auth = JSON.parse(root.auth);
    return auth?.token?.token ?? null;
  } catch {
    return null;
  }
};

// The bearer the FAILED request actually sent (set by baseQuery.prepareHeaders
// from the store token at request time). We compare the persisted token against
// THIS, not the current store token: if adoption already advanced the store,
// comparing store-vs-persisted would look equal and wrongly log out.
const extractRequestBearer = (request: unknown): string | null => {
  try {
    const header = (request as Request | undefined)?.headers?.get?.('authorization');
    if (!header) return null;
    return header.replace(/^Bearer\s+/i, '') || null;
  } catch {
    return null;
  }
};

// Auth endpoints that can legitimately return 401 (bad credentials, expired
// reset link, etc.). A 401 from one of these is a normal form-validation
// failure shown inline by the UI — it must NOT trigger a session-expired
// logout, otherwise a wrong-password sign-in would log the user out / loop.
const authEndpointRe = /\/(signin|sign-up|forgot-password|reset-password|validate-token|verify-mail-otp|verify-mfa-otp|verify-lock-password)/;
const authEndpointNames = [
  'signIn',
  'signUpAction',
  'verifyAction',
  'resendVerifyAction',
  'resetPasswordLinkAction',
  'resetPasswordAction',
  'validateToken',
  'verifyMfaAction',
  'verifyLockPassword',
];

// Endpoints where a 404 means "that document is already gone", not a bug.
// Opening or acting on a document that was deleted elsewhere is ordinary use
// of a multi-device app, and the screens handle it; reporting each one filled
// Sentry with DOQIT-APP-17/18 and friends across four users.
const expected404Endpoints = [
  'editDocumentDetailsAction',
  'deleteCategoryDocumentAction',
  'getDocumentShares',
  'getPublicLinks',
];

export const rtkQueryErrorHandler: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const status = action.payload?.status;

    // Only report unexpected errors (5xx, network, etc.) to Sentry. Expected
    // client errors (401 expired session, 403, 422 validation, 429 rate limit)
    // are normal app flow handled by the UI and would otherwise be huge noise.
    // Auth endpoints return their validation failures as 400 (wrong MFA code,
    // bad credentials) — those are user error shown inline, not bugs.
    const captureEndpointName = action.meta?.arg?.endpointName;
    const isExpectedAuth400 = status === 400 && authEndpointNames.includes(captureEndpointName);
    const isExpected404 = status === 404 && expected404Endpoints.includes(captureEndpointName);
    if (!EXPECTED_CLIENT_ERROR_STATUSES.includes(status) && !isExpectedAuth400 && !isExpected404) {
      const endpointName = captureEndpointName;
      // Fingerprint by status + endpoint so genuine failures (e.g. a 500 on a
      // specific endpoint) surface as distinct Sentry issues instead of all
      // bucketing under the generic "API Error: api/executeQuery/rejected".
      captureError(new Error(`API ${status ?? 'ERR'} ${endpointName ?? action.type}`), {
        status,
        endpoint: endpointName,
        payload: action.payload,
      }, ['rtk-query-error', String(status ?? 'unknown'), String(endpointName ?? action.type)]);
    }

    if (status === 401) {
      // Was this 401 from one of the auth endpoints themselves? Those 401s are
      // expected (e.g. wrong password) and are surfaced inline by the calling
      // screen — clearing creds / toasting "session expired" there would be
      // wrong and could cause a logout loop. Gate on the actual request URL,
      // falling back to the RTK endpointName when the URL isn't available.
      const reqUrl: string = action.meta?.baseQueryMeta?.request?.url ?? '';
      const endpointName: string = action.meta?.arg?.endpointName ?? '';
      const isAuthCall = (reqUrl !== '' && authEndpointRe.test(reqUrl))
        || authEndpointNames.includes(endpointName);

      if (!isAuthCall) {
        // Last-chance recovery before logout. validate-token ROTATES the
        // bearer, so another tab (or an earlier request in this tab) may have
        // already minted a newer token that landed in persist:root after this
        // request was sent. If persist:root holds a token that differs from
        // the one THIS request used, the 401 is stale — adopt the newer token
        // and let subsequent requests retry with it. The failed request stays
        // failed; we skip the logout/reset/toast for this action.
        const failedBearer = extractRequestBearer(action.meta?.baseQueryMeta?.request);
        const persistedToken = readPersistedToken();
        const hasNewerToken = !!persistedToken && persistedToken !== failedBearer;

        if (hasNewerToken) {
          // Adopt only the token (leaves user/persist intact). If this adopted
          // token itself later 401s, that request's bearer will equal the
          // persisted token, so hasNewerToken is false and we log out normally
          // — no thrash loop.
          api.dispatch(refreshToken({ token: { token: persistedToken as string } }));
        } else {
          // An authenticated call 401'd and there is no newer token → the
          // session has genuinely expired. Clear credentials (this also drops
          // the persisted native token); the app's auth-gated routes then send
          // the user back to /login.
          trackEvent(MixpanelEventName.sessionExpired, { endpoint: endpointName });
          api.dispatch(clearCredentials());
          api.dispatch(baseApi.util.resetApiState());
          // `id` de-dupes so a burst of 401s shows a single toast (react-hot-toast).
          toast.error('Your session has expired. Please sign in again.', { id: 'session-expired' });
        }
      }
    }
  }

  return next(action);
};
